/**
 * Mock Data Utilities
 *
 * Helper functions for generating and managing mock data.
 */

/**
 * Check if we're on a staging or production environment
 * Used to ensure mock data NEVER runs in staging/production
 */
export function isStageOrProd(): boolean {
  if (typeof window === "undefined") {
    // Server-side: check environment variables
    const vercelEnv = process.env.VERCEL_ENV;
    if (vercelEnv === "production" || vercelEnv === "preview") {
      return true;
    }
    return process.env.NODE_ENV === "production";
  }

  // Client-side: check hostname
  const hostname = window.location.hostname;
  const stagingPatterns = [
    "vercel.app",
    "stage.",
    "staging.",
    "preview.",
    "-stage.",
    "-staging.",
  ];

  // Check if hostname matches any staging pattern
  for (const pattern of stagingPatterns) {
    if (hostname.includes(pattern)) {
      return true;
    }
  }

  // Check if it's a production domain (not localhost)
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const isProductionDomain = !isLocalhost && !hostname.includes(".local");

  return isProductionDomain;
}

/**
 * Check if demo mode is enabled via localStorage
 * Used to enable mock data for demo@sigmavue.com users
 */
export function isDemoModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("sigmavue_demo_mode") === "true";
}

/**
 * Enable demo mode (set localStorage flag)
 * Called during demo login
 */
// PR-218: same-tab listeners can't rely on the `storage` event (it only fires
// in OTHER tabs). Dispatch an explicit event whenever demo mode changes so the
// banner (and any same-tab consumer) updates immediately.
export const DEMO_MODE_CHANGE_EVENT = "sv-demo-mode-change";
function notifyDemoModeChange(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(DEMO_MODE_CHANGE_EVENT));
  }
}

export function enableDemoMode(): void {
  if (typeof window !== "undefined") {
    localStorage.setItem("sigmavue_demo_mode", "true");
    window.__SV_DEMO_MOCK__ = true;
    notifyDemoModeChange();
  }
}

export function resetDemoOnboardingSession(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem("sigmavue_demo_onboarding_session");
  }
}

export function seedCompletedDemoOnboardingSession(): void {
  if (typeof window === "undefined") return;
  const now = new Date().toISOString();
  localStorage.setItem(
    "sigmavue_demo_onboarding_session",
    JSON.stringify({
      id: "demo-onboarding",
      questionnaireVersion: 2,
      status: "completed",
      current_step: 6,
      branch: {},
      answers: {
        userName: "Local Admin",
        username: "localadmin",
        hasAccounts: true,
        accountType: "prop_firm",
        propFirm: "Apex Trader Funding",
        propFirmId: "apex",
        propSizes: ["$50K PA"],
        accountConnectionMode: "tradovate_sim",
        dailyTarget: 2500,
        automation: "fully_automated",
        reviewedSetup: true,
      },
      skipState: {},
      requiredReviewPayload: {
        account_skipped: false,
        strategy_skipped: false,
        selected_lane: "prop_safe",
        selected_prop_firm: "Apex Trader Funding",
        selected_account_size: 50000,
        connection_mode: "tradovate_sim",
        confirmed: true,
      },
      planContext: {},
      selectedOnboardingKey: null,
      guardrailsConfig: {},
      createdAt: now,
      updatedAt: now,
    }),
  );
}

/**
 * Disable demo mode (clear localStorage flag)
 * Called on logout or when switching to real account
 */
export function disableDemoMode(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem("sigmavue_demo_mode");
    window.__SV_DEMO_MOCK__ = false;
    notifyDemoModeChange();
  }
}

/**
 * Check if mock data should be enabled
 * Only enabled when demo mode is explicitly enabled via localStorage.
 *
 * SAFETY: This function includes multiple safeguards to ensure
 * mock data NEVER runs in staging or production environments for real users.
 * Demo mode is only available on localhost.
 */
export function shouldUseMockData(): boolean {
  // Server-side: never use mock data (no localStorage access for demo mode check)
  if (typeof window === "undefined") {
    return false;
  }

  // Client-side: check localhost + demo mode
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  // Demo mode: only on localhost when explicitly enabled
  if (isLocalhost && isDemoModeEnabled()) {
    return true;
  }

  // LIVE DATA ONLY POLICY:
  // Mock data is disabled in all other environments to enforce live API usage.
  return false;
}

/**
 * Generate a random number within a range
 */
export function randomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/**
 * Generate a random integer within a range
 */
export function randomIntInRange(min: number, max: number): number {
  return Math.floor(randomInRange(min, max + 1));
}

/**
 * Generate a random price movement (simulates market tick)
 */
export function generatePriceMovement(
  basePrice: number,
  volatility: number = 0.001,
): number {
  const movement = basePrice * volatility * (Math.random() - 0.5) * 2;
  return parseFloat((basePrice + movement).toFixed(2));
}

/**
 * Generate a random PnL value that simulates realistic trading
 */
export function generateMockPnL(
  openPrice: number,
  currentPrice: number,
  volume: number,
  direction: "long" | "short",
  tickValue: number = 12.5, // Default for ES futures
): number {
  const priceDiff =
    direction === "long" ? currentPrice - openPrice : openPrice - currentPrice;

  // Convert price difference to ticks (assuming 0.25 tick size for ES)
  const ticks = priceDiff / 0.25;
  const pnl = ticks * tickValue * volume;

  return parseFloat(pnl.toFixed(2));
}

/**
 * Generate a random date within the last N days
 */
export function randomDateWithinDays(days: number): Date {
  const now = new Date();
  const daysAgo = randomIntInRange(0, days);
  const hoursAgo = randomIntInRange(0, 23);
  const minutesAgo = randomIntInRange(0, 59);

  const date = new Date(now);
  date.setDate(date.getDate() - daysAgo);
  date.setHours(now.getHours() - hoursAgo);
  date.setMinutes(now.getMinutes() - minutesAgo);

  return date;
}

/**
 * Generate a unique ID
 */
export function generateMockId(): string {
  return `mock_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Generate a broker position ID
 */
export function generateBrokerPositionId(): string {
  return `${randomIntInRange(100000000, 999999999)}`;
}

/**
 * Simulate network delay for realistic UX
 * Reduced delays for snappier development experience
 */
export async function simulateNetworkDelay(
  minMs: number = 50,
  maxMs: number = 150,
): Promise<void> {
  const delay = randomIntInRange(minMs, maxMs);
  return new Promise((resolve) => setTimeout(resolve, delay));
}

/**
 * Format currency for display
 */
export function formatMockCurrency(value: number): string {
  const prefix = value >= 0 ? "+" : "";
  return `${prefix}$${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Trading instruments available for mock data
 */
export const MOCK_INSTRUMENTS = [
  {
    symbol: "ESZ4",
    name: "E-mini S&P 500",
    category: "futures",
    tickSize: 0.25,
    tickValue: 12.5,
    valuePerPoint: 50,
  },
  {
    symbol: "NQZ4",
    name: "E-mini Nasdaq-100",
    category: "futures",
    tickSize: 0.25,
    tickValue: 5.0,
    valuePerPoint: 20,
  },
  {
    symbol: "YMZ4",
    name: "E-mini Dow",
    category: "futures",
    tickSize: 1.0,
    tickValue: 5.0,
    valuePerPoint: 5,
  },
  {
    symbol: "RTY",
    name: "E-mini Russell 2000",
    category: "futures",
    tickSize: 0.1,
    tickValue: 5.0,
    valuePerPoint: 50,
  },
  {
    symbol: "CLZ4",
    name: "Crude Oil",
    category: "futures",
    tickSize: 0.01,
    tickValue: 10.0,
    valuePerPoint: 1000,
  },
  {
    symbol: "GCZ4",
    name: "Gold",
    category: "futures",
    tickSize: 0.1,
    tickValue: 10.0,
    valuePerPoint: 100,
  },
  {
    symbol: "6EZ4",
    name: "Euro FX",
    category: "futures",
    tickSize: 0.00005,
    tickValue: 6.25,
    valuePerPoint: 125000,
  },
  {
    symbol: "MESZ4",
    name: "Micro E-mini S&P 500",
    category: "futures",
    tickSize: 0.25,
    tickValue: 1.25,
    valuePerPoint: 5,
  },
  {
    symbol: "MNQZ4",
    name: "Micro E-mini Nasdaq-100",
    category: "futures",
    tickSize: 0.25,
    tickValue: 0.5,
    valuePerPoint: 2,
  },
] as const;

/**
 * Get a random instrument
 */
export function getRandomInstrument() {
  return MOCK_INSTRUMENTS[randomIntInRange(0, MOCK_INSTRUMENTS.length - 1)];
}

/**
 * Generate realistic base prices for instruments
 */
export const MOCK_BASE_PRICES: Record<string, number> = {
  ESZ4: 5925.5,
  NQZ4: 20680.25,
  YMZ4: 44250.0,
  RTY: 2285.4,
  CLZ4: 68.45,
  GCZ4: 2635.2,
  "6EZ4": 1.0525,
  MESZ4: 5925.5,
  MNQZ4: 20680.25,
};

/**
 * Get base price for an instrument
 */
export function getBasePrice(symbol: string): number {
  return MOCK_BASE_PRICES[symbol] || 100.0;
}
